<template>
  <b-container
    :fluid="content.width === 'full-width'"
    :class="{
      'px-0': content.width === 'full-width',
      [content.additionalClasses]: true
    }"
  >
    <b-row :no-gutters="content.width === 'full-width'">
      <b-col cols="12">
        <div class="video-banner-box-outer">
          <div
            class="video-banner-box"
            :class="[displayAspectRatio, displayBanner]"
          >
            <!-- portal for video-modal -->
            <template v-if="hasModalVideo">
              <VideoModal
                :show="showVideoModal"
                :video-url="content.video_link.video_url"
                @close="showVideoModal = false"
                @stop="play"
              />
              <transition
                appear
                name="fade"
              >
                <div
                  v-if="!backgroundVideoIsPlaying"
                  class="video-banner-tint-overlay"
                />
              </transition>
            </template>
            <div
              class="video-banner-tint video-banner-tint-dark"
              :style="{ opacity: tint }"
            />
            <div class="effect-layer">
              <video
                ref="video_element"
                playsinline
                muted="muted"
                loop="loop"
                class="video-banner-display"
                :class="displayVideoFocus"
                :autoplay="true"
                :poster="videoPoster"
              >
                <template v-if="noMotionPreferences">
                  <source
                    :src="content.background_video.video_mp4"
                    type="video/mp4"
                  >
                </template>
                <p>
                  Sorry, there's a problem playing this video. Please try using a different browser.
                </p>
              </video>
            </div>

            <div class="controls">
              <div class="video-banner-item-inner">
                <template
                  v-if="
                    content.description ||
                      content.title ||
                      content.leadline ||
                      hasBannerGraphic ||
                      content.hasPrimaryAction ||
                      content.hasSecondaryAction ||
                      hasModalVideo
                  "
                >
                  <div
                    class="video-banner-content"
                    :class="[
                      content.BannerVerticalPosition,
                      content.BannerHorizontalPosition,
                    ]"
                  >
                    <b-container>
                      <b-row>
                        <b-col>
                          <div
                            class="video-banner-content-inner"
                            :class="{
                              'px-3 px-md-5': content.width !== 'full-width'
                            }"
                          >
                            <div
                              class="video-banner-content-text"
                              :class="content.BannerTextAlignment"
                            >
                              <VideoBoxLogo
                                v-if="hasBannerGraphic"
                                :content="bannerGraphicSettings"
                              />
                              <div
                                v-if="content.leadline"
                                class="video-banner-leadline"
                              >
                                <h4>
                                  {{ content.leadline }}
                                </h4>
                              </div>
                              <div
                                v-if="content.title"
                                class="video-banner-title"
                              >
                                <h1>
                                  {{ content.title }}
                                </h1>
                              </div>
                              <div
                                v-if="content.description"
                                class="video-banner-description"
                              >
                                {{ content.description }}
                              </div>
                              <div
                                v-if="content.hasPrimaryAction || content.hasSecondaryAction"
                                class="video-banner-actions actions"
                              >
                                <div
                                  v-if="content.hasPrimaryAction"
                                  class="video-banner-action-item"
                                >
                                  <HighlightButton
                                    :url="content.primaryAction.linkUrl"
                                    :title="content.primaryAction.linkText"
                                    :icon="content.primaryAction.buttonIcon"
                                    :button-type="content.primaryAction.buttonType"
                                    :button-size="content.primaryAction.buttonSize"
                                    :button-style="content.primaryAction.buttonStyle"
                                    :icon-position="content.primaryAction.buttonIconPosition"
                                  />
                                </div>
                                <div
                                  v-if="content.hasSecondaryAction"
                                  class="video-banner-action-item"
                                >
                                  <HighlightButton
                                    :url="content.secondaryAction.linkUrl"
                                    :title="content.secondaryAction.linkText"
                                    :icon="content.secondaryAction.buttonIcon"
                                    :button-type="content.secondaryAction.buttonType"
                                    :button-size="content.secondaryAction.buttonSize"
                                    :button-style="content.secondaryAction.buttonStyle"
                                    :icon-position="content.secondaryAction.buttonIconPosition"
                                  />
                                </div>
                              </div>

                              <div>
                                <HighlightButton
                                  v-if="hasModalVideo"
                                  :disabled="!backgroundVideoIsPlaying"
                                  button-type="square"
                                  icon="triangle-right"
                                  class="video-banner-video-action"
                                  @click.native="openModalVid"
                                />
                              </div>
                            </div>
                          </div>
                        </b-col>
                      </b-row>
                    </b-container>
                  </div>
                </template>
              </div>
            </div>

            <template>
              <generic-link
                v-if="hasFullBannerLink"
                class="full-banner-link"
                :href="content.fullBannerLink.linkUrl"
              />
            </template>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import GenericLink from '../ui-elements/generic-link.vue'
import HighlightButton from 'dev/common-components/src/components/highlight-button/highlight-button'
import VideoModal from './subcomponents/VideoBoxModal.vue'
import VideoBoxLogo from './subcomponents/VideoBoxLogo.vue'

export default {
  name: 'videobox',
  components: {
    VideoModal,
    HighlightButton,
    VideoBoxLogo,
    GenericLink
  },
  props: {
    filestore: {
      type: Object,
      required: true
    },
    widget: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      openVideoDetails: false,
      backgroundVideoIsPlaying: true,
      showVideoModal: false,
      show: false
      // onlyOnHover: false, //if set, we only show play the video on hover
    }
  },
  computed: {
    videoPoster() {
      try {
        const posterSettings = this.content.video_placeholder_background

        return posterSettings.opmode === 'filestore'
          ? posterSettings.fileStore.endpoint.concat('/', posterSettings.fileStore.fileStoreID, posterSettings.fileStore.filepath)
          : posterSettings.url
      }
      catch {
        return ''
      }
    },
    bannerGraphicSettings() {
      try {
        const settings = this.content.banner_graphic
        const imageSettings = settings.image

        const url = imageSettings.opmode === 'filestore'
          ? imageSettings.fileStore.endpoint.concat('/', imageSettings.fileStore.fileStoreID, '/', imageSettings.fileStore.filepath)
          : imageSettings.url

        return {
          image_url: url,
          alt_text: imageSettings.fileStore.description,
          title_text: '',
          image_size: settings.imageSize,
          image_size_custom_width: settings.imageSizeCustomWidth
        }
      }
      catch {
        return {}
      }
    },
    tint() {
      const tint = Number(this.content.videoTint)

      return (!isNaN(tint) && tint !== 0)
        ? tint / 100
        : 0
    },
    content() {
      return this.widget.widgetSettings.videoSettings
    },
    // check to see if there's a youtube or vimeo link
    hasModalVideo() {
      return this.content.displayPopupVideo && this.content.video_link.video_url
    },
    displayBanner() {
      return 'banner'
    },
    displayVideoFocus() {
      return this.content.videoFocus
    },
    // needs better check
    hasFullBannerLink() {
      try {
        return this.content.fullBannerLink.linkUrl
      }
      catch {
        return false
      }
    },
    hasBannerGraphic() {
      return this.content.banner_graphic.enabled
    },
    displayAspectRatio() {
      return (this.content.video_aspect_ratio !== null)
        ? `aspect-ratio-${this.content.video_aspect_ratio}`
        : 'aspect-ratio-fluid'
    },
    isSSR: function() {
      return this.$ssrContext !== undefined
    },
    noMotionPreferences() {
      if (this.isSSR || !window) return true

      return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
  },
  methods: {
    openModalVid() {
      this.pause()
      this.showVideoModal = true
    },
    play() {
      this.$refs.video_element.play()
      this.backgroundVideoIsPlaying = true
    },
    pause() {
      this.$refs.video_element.pause()
      this.backgroundVideoIsPlaying = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@scss/custom.scss";

.inner-image {
  width: 100%;
  height: auto;
}

.full-banner-link {
  z-index: var(--full-banner-link-z-index);
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
}
.video-banner-box {
  --main-banner-element-margin: 0.5em;
  --main-banner-padding: 3.5em;

  --main-video-banner-content-text-color: #fff;
  --main-banner-action-element-margin: 1em;

  --main-video-banner-content-text-width:100%;
  --main-video-banner-leadline-font-size:1.4em;
  --main-video-banner-title-font-size: 3em;
  --main-video-banner-description-font-size: 1.1em;

  --video-banner-tint-background-color: var(--neutral-tint, #000);
  --video-banner-tint-opacity: 0.1;
  --full-banner-link-z-index: 5;
  --video-banner-content-text-z-index: 6;
  --video-banner-content-link-z-index: 7;
  --video-banner-tint-z-index: 3;
  @include media-breakpoint-up(md) {
    --main-banner-element-margin: 1em;
  }

  @include media-breakpoint-up(lg) {
    --main-banner-element-margin: 1.5em;
  }

  &.banner {
    @include media-breakpoint-up(sm) {
    --main-video-banner-content-text-width:80%;
    }
    @include media-breakpoint-up(md) {
    --main-video-banner-content-text-width:70%;
    }
    @include media-breakpoint-up(lg) {
    --main-video-banner-content-text-width:65%;
    }
    --video-fluid-min-height: 400px;

    @include media-breakpoint-up(md) {
      --video-fluid-min-height: 800px;
    }

    @include media-breakpoint-up(lg) {
      --video-fluid-min-height: 900px;
    }
  }
}

.video-banner-box-outer {
  height: 100%;
  display: flex;
  .video-banner-box {
    height: 100%;
    width: 100%;
    display: flex;
  }
}

.video-banner-box {
  position: relative;
  overflow: hidden;
}
.video-banner-display {
  position: absolute;
}
.video-banner-actions {
  .video-banner-action-item {
    &:not(:first-child),
    &:not(:last-child) {
      margin: var(--main-banner-action-element-margin) 0.5em 0em 0.5em; //note, don't use padding here
    }
  }
  &.actions {
    margin-left: -0.5em;
    margin-right: -0.5em;
    //margin-top: calc(var(--main-banner-action-element-margin) * -1);
  }
}

.controls {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  .info {
    margin-left: 1em;
    color: var(--vc-text-color);

    .title {
      margin: 0;
    }
  }

  .video-banner-video-action {
    z-index: var(--video-banner-content-link-z-index);
    position: relative;
    pointer-events: all;
    outline: none;
    border: 0px;
    color: var(--vc-highlight-action-text-color);
    background-color: var(--vc-highlight-action-background-color);
    width: 5em;
    height: 5em;
    border-radius: 100%;
    justify-content: center;
    align-items: center;
    display: inline-flex;
    transform-origin: center;
    transition: transform 0.3s ease-in-out;
    box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.2);

    &:hover {
      transform: scale(1.2);
      transform-origin: center;
    }
    .control-icon {
      width: 1.5em;
      height: 1.5em;
      fill: var(--vc-highlight-action-text-color);
    }
    &:focus {
      transform: scale(1.15);
    }
  }
}

//banner content position
.video-banner-item-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-clip: border-box;
  height: 100%;
  width: 100%;
}

.video-banner-content-text {
  width: var(--main-video-banner-content-text-width);
}

.video-banner-description {
  font-size: var(--main-video-banner-description-font-size);
}

.video-banner-content {
  flex-direction: column;
  height: 100%;
  z-index: var(--content-z-index);
  display: flex;
  color: var(--main-video-banner-content-text-color);
}

.video-banner-leadline h4 {
  font-family: var(--vc-heading-font);
  font-size: var(--main-video-banner-leadline-font-size);
  margin:0;
}
.video-banner-title h1 {
  font-family: var(--vc-heading-font);
  font-size: var(--main-video-banner-title-font-size);
  margin:0;
}

.video-banner-content-text {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: var(--video-banner-content-text-z-index);
  pointer-events: none;

  //this is an edge case for making allowance for any links used inside the content text area over the top of a full banner link

  .video-banner-actions {
    a {
      z-index: var(--video-banner-content-link-z-index);
      position: relative;
      pointer-events: all;
    }
  }
  .video-banner-description {
    ::v-deep a {
      color: var(--main-video-banner-content-text-color);
      z-index: var(--video-banner-content-link-z-index);
      text-decoration: underline;
      position: relative;
      pointer-events: all;
    }
  }
  > * + * {
    margin-top: var(--main-banner-element-margin);
  }
}

//banner text
//we center banner content on mobile regardless of configured settings
.video-banner-text-center, .video-banner-text-left, .video-banner-text-right {
  text-align: center;
  justify-content: center;
  align-items: center;
}

@include media-breakpoint-up(sm) {
  .video-banner-text-left {
    text-align: left;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .video-banner-text-right {
    text-align: right;
    justify-content: flex-end;
    align-items: flex-end;
  }
}

//video-banner-actions

.video-banner-actions {
  display: flex;
  justify-content: inherit;
}

.video-banner-content-inner {
  display: flex;
}
//banner content positioning
//horizontal
.video-banner-content-horizontal {
  &-position-left {
    justify-content: flex-start;
  }
  &-position-right {
    justify-content: flex-end;
    .video-banner-content-inner {
      justify-content: flex-end;
    }
  }
  &-position-centered {
    .video-banner-content-inner {
      justify-content: center;
    }
    .row {
      justify-content: center;
    }
  }
}
//vertical
.video-banner-content-vertical {
  &-position-bottom {
    justify-content: flex-end;
    padding-top: calc(var(--main-banner-padding) * 1.5);
    padding-bottom: calc(var(--main-banner-padding) * 1);
    @include media-breakpoint-up(md) {
      padding-top: calc(var(--main-banner-padding) * 3);
      padding-bottom: calc(var(--main-banner-padding) * 1);
    }
    @include media-breakpoint-up(lg) {
      padding-top: calc(var(--main-banner-padding) * 4);
      padding-bottom: calc(var(--main-banner-padding) * 1.5);
    }
  }
  &-position-centered,
  &-position-center {
    justify-content: center;
    padding-top: calc(var(--main-banner-padding) * 1.5);
    padding-bottom: calc(var(--main-banner-padding) * 1.5);

    @include media-breakpoint-up(md) {
      padding-top: calc(var(--main-banner-padding) * 2);
      padding-bottom: calc(var(--main-banner-padding) * 2);
    }

    @include media-breakpoint-up(lg) {
      padding-top: calc(var(--main-banner-padding) * 3);
      padding-bottom: calc(var(--main-banner-padding) * 3);
    }
  }

  &-position-top {
    padding-top: calc(var(--main-banner-padding) * 1);
    padding-bottom: calc(var(--main-banner-padding) * 1.5);

    @include media-breakpoint-up(md) {
      padding-top: calc(var(--main-banner-padding) * 1.5);
      padding-bottom: calc(var(--main-banner-padding) * 3);
    }

    @include media-breakpoint-up(lg) {
      padding-top: calc(var(--main-banner-padding) * 1.5);
      padding-bottom: calc(var(--main-banner-padding) * 4);
    }
  }
}

//tint
.video-banner-tint {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 2;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
}
.video-banner-tint-dark {
  background-color: var(--neutral-tint);
}

//aspect ratio mixin and styling
@mixin responsive-ratio($x, $y, $pseudo: false) {
  $padding: unquote(($y / $x) * 100 + "%");
  @if $pseudo {
    &:before {
      @include pseudo($pos: relative);
      width: 100%;
      padding-bottom: $padding;
    }
  } @else {
    padding-bottom: $padding;
  }
}

//we deal with a reduced set of aspect ratios ratios here
//these are typically the most supported sizes in video production pipelines for web based content
//feel free to add more
//in the viewport height one, this consumes the entire viewport height of the screen for "full screen videos"

.aspect-ratio {
  &-fluid,
  &-viewport-height {
    .controls {
      position: relative;
      height: auto;
    }
  }
}
.aspect-ratio {
  &-fluid {
    height: 100%;
    min-height: var(--video-fluid-min-height);
    .controls {
      min-height: var(--video-fluid-min-height);
    }
  }
  &-viewport-height {
    min-height: 100vh;
    padding: 0;
  }
  &-1-1 {
    height: 0;
    @include responsive-ratio(1, 1);
  }
  &-16-9 {
    height: 0;
    @include responsive-ratio(16, 9);
  }
  &-16-10 {
    height: 0;
    @include responsive-ratio(16, 10);
  }
  &-4-3 {
    height: 0;
    @include responsive-ratio(4, 3);
  }
}

//video element

//iniialize the video to display in cover format on the element
//if the object's aspect ratio, both the video itself and the poster in this case, does not match the aspect ratio of its box, then the object will be clipped to fit.
.video-banner-display {
  object-fit: cover;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;

  //in combination with the object fit above
  //specifies the alignment of the selected replaced element's contents within the video display box, in this case with cover, it's more of a vertical focus, as the width is more often than not 100%
  &.bottom {
    object-position: 50% 100%;
  }
  &.centered {
    object-position: 50% 50%;
  }
  &.top {
    object-position: 50% 0%;
  }
}

//styling for overlay element that gets displayed when the video is paused
.video-banner-tint-overlay {
  background: var(--video-banner-tint-background-color);
  opacity: var(--video-banner-tint-opacity);
  z-index: var(--video-banner-tint-z-index);
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
}

//animations
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
